import { FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faPhone, faEnvelope, faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import Client, { LeadSource } from '@spike/client-model';
import { formatPhoneNumber } from '@spike/phone-utils';
import Button from 'components/UI/V3/Button/Button';
import ThreeDotsDropdown from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdown';
import { ThreeDotsDropdownOption } from 'components/UI/V3/ThreeDotsDropdown/ThreeDotsDropdownOption';
import { useMarketplace } from 'hooks';
import styles from './LeadDetailHeader.module.scss';

const SOURCE_LABELS: Partial<Record<LeadSource, string>> = {
    [LeadSource.Manual]: 'Manually',
    [LeadSource.IntakeForm]: 'Intake Form',
    [LeadSource.AbandonedCart]: 'Abandoned Cart',
    [LeadSource.UnknownSms]: 'Unknown Number',
    [LeadSource.Import]: 'Import'
};

interface LeadDetailHeaderProps {
    lead: Client;
    actions: Array<ThreeDotsDropdownOption>;
    onBack: () => void;
    onConvert: () => void;
}

const LeadDetailHeader: FunctionComponent<LeadDetailHeaderProps> = ({ lead, actions, onBack, onConvert }) => {
    const marketplace = useMarketplace();

    const fullName = `${lead.firstName} ${lead.lastName}`.trim() || '-';

    const sourceLabel = lead.leadSource
        ? SOURCE_LABELS[lead.leadSource] ?? String(lead.leadSource).replace(/_/g, ' ')
        : undefined;

    const canConvert = lead.clientSince == null;

    return (
        <header className={styles.header}>
            <button
                type="button"
                className={styles.backButton}
                aria-label="Back to leads"
                onClick={onBack}
            >
                <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <div className={styles.identity}>
                <h2 className={styles.name}>{fullName}</h2>

                {sourceLabel && <span className={styles.sourceBadge}>{sourceLabel}</span>}

                <div className={styles.contactRow}>
                    {lead.phone && (
                        <span className={styles.contactItem}>
                            <FontAwesomeIcon icon={faPhone} />
                            {formatPhoneNumber(lead.phone, marketplace.basics.address.country!.id)}
                        </span>
                    )}
                    {lead.email && (
                        <span className={styles.contactItem}>
                            <FontAwesomeIcon icon={faEnvelope} />
                            {lead.email}
                        </span>
                    )}
                </div>
            </div>

            <div className={styles.topActions}>
                {canConvert && (
                    <Button
                        id="lead_detail_convert_to_client"
                        className={styles.convertButton}
                        label="Convert to Client"
                        size="large"
                        leftIcon={faUserPlus}
                        onClick={onConvert}
                    />
                )}
                <ThreeDotsDropdown
                    className={styles.dotsButton}
                    options={actions}
                    direction="down"
                />
            </div>
        </header>
    );
};

export default LeadDetailHeader;
